import { Lightbulb, Users, CheckCircle, BookOpen, Zap, Sparkles } from 'lucide-react';

export default function About() {
  const mainFeatures = [
    {
      icon: Lightbulb,
      title: 'Structured Learning',
      description: 'Planned routine for homework, revision, and concept clarity',
      color: 'yellow'
    },
    {
      icon: Sparkles,
      title: 'Skill Development Programs',
      description: 'Special focus on communication, maths speed, writing, creativity, and reading',
      benefits: ['Builds confidence in overall personality', 'Develops essential life skills from an early age', 'Helps children perform better in school and beyond'],
      color: 'orange'
    },
    {
      icon: Users,
      title: 'Individual Growth',
      description: 'Every child receives attention based on their learning level',
      benefits: ['Focus on weak areas and improvement', 'Regular progress observation', 'Encourages self-confidence and independence'],
      color: 'green'
    },
  ];

  const additionalFeatures = [
    {
      icon: CheckCircle,
      title: 'Daily Homework Completion',
      description: 'Children complete all school work under guidance',
      benefits: ['Reduces parents\' academic stress', 'Ensures better understanding of school concepts'],
      color: 'pink'
    },
    {
      icon: BookOpen,
      title: 'Activity-Based Learning',
      description: 'Learning through practical, engaging, and fun methods',
      benefits: ['Keeps children interested and active', 'Improves understanding through real practice'],
      color: 'sky'
    },
    {
      icon: Zap,
      title: 'Discipline & Routine',
      description: 'Builds regular study habits and time management',
      benefits: ['Develops responsibility in children', 'Creates a strong daily learning habit'],
      color: 'purple'
    },
  ];

  return (
    <section id="about" className="py-24 lg:py-6 bg-pink-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-display text-purple-dark mb-4">
            About <span className="text-purple-primary">KinderHeaven</span>
          </h2>
          <p className="text-gray-600 text-base sm:text-lg max-w-2xl mx-auto">
            A complete after-school learning space for academic excellence and skill development
          </p>
        </div>

        {/* Main Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
          {mainFeatures.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div key={index} className="bg-white rounded-2xl p-6 sm:p-8 shadow-soft hover:shadow-lg transition-shadow duration-300">
                <div className="bg-gradient-to-br from-purple-primary/10 to-pink-light/20 rounded-full w-16 h-16 flex items-center justify-center mb-4">
                  <Icon className="text-purple-dark" size={32} />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2 font-display">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  {feature.description}
                </p>
                {feature.benefits && (
                  <ul className="space-y-2">
                    {feature.benefits.map((benefit, idx) => (
                      <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                        <span className="text-purple-primary font-bold mt-1">✓</span>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>

        {/* Additional Features */}
        <div>
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-8 text-center font-display">
            Additional <span className="text-purple-primary">Features</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {additionalFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-white rounded-2xl p-6 sm:p-8 shadow-soft hover:shadow-lg transition-shadow duration-300">
                  <div className="bg-gradient-to-br from-purple-primary/20 to-pink-light/30 w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="text-purple-primary" size={28} />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 font-display">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-4">
                    {feature.description}
                  </p>
                  <ul className="space-y-2">
                    {feature.benefits.map((benefit, idx) => (
                      <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                        <span className="text-purple-primary font-bold">•</span>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
